import { useEffect, useState } from 'react'

type StableViewport = {
  width: number
  height: number
  largeHeight: number
  isMobile: boolean
}

type ViewportMeasurements = {
  width: number
  height: number
  largeHeight: number
}

const MOBILE_BREAKPOINT = 768

const getInitialViewportSnapshot = (
  _mobileBreakpoint: number
): StableViewport => ({
  width: 0,
  height: 0,
  largeHeight: 0,
  isMobile: true
})

const setDocumentViewportUnit = ({
  smallHeight,
  largeHeight
}: {
  smallHeight: number
  largeHeight: number
}) => {
  if (typeof document === 'undefined') return

  document.documentElement.style.setProperty('--vh', `${smallHeight * 0.01}px`)
  document.documentElement.style.setProperty('--lvh', `${largeHeight * 0.01}px`)
}

const getViewportMeasurements = (): ViewportMeasurements => {
  if (typeof window === 'undefined') {
    return {
      width: 0,
      height: 0,
      largeHeight: 0
    }
  }

  const width = window.innerWidth
  const isMobile = width < MOBILE_BREAKPOINT
  const height = Math.round(
    window.visualViewport?.height || window.innerHeight
  )
  const largeHeight = Math.round(
    Math.max(window.innerHeight, window.visualViewport?.height || (isMobile ? screen.height : 0))
  )

  return {
    width,
    height,
    largeHeight
  }
}

const useStableViewport = (mobileBreakpoint = MOBILE_BREAKPOINT) => {
  const [viewport, setViewport] = useState<StableViewport>(() =>
    getInitialViewportSnapshot(mobileBreakpoint)
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    let stableHeight = 0
    let largeHeight = 0
    let viewportKey = ''

    const updateViewport = () => {
      const {
        width: rawWidth,
        height: rawHeight,
        largeHeight: nextLargeHeight
      } = getViewportMeasurements()
      const isMobile = rawWidth < mobileBreakpoint
      const orientation = rawWidth > rawHeight ? 'landscape' : 'portrait'
      const nextKey = `${isMobile ? 'mobile' : 'desktop'}-${orientation}-${Math.round(rawWidth)}`

      if (!isMobile) {
        stableHeight = rawHeight
        largeHeight = nextLargeHeight
        viewportKey = nextKey
        setDocumentViewportUnit({
          smallHeight: rawHeight,
          largeHeight: nextLargeHeight
        })

        setViewport((prev) => {
          if (
            prev.width === rawWidth &&
            prev.height === rawHeight &&
            prev.largeHeight === nextLargeHeight &&
            prev.isMobile === isMobile
          ) {
            return prev
          }

          return {
            width: rawWidth,
            height: rawHeight,
            largeHeight: nextLargeHeight,
            isMobile
          }
        })

        return
      }

      if (viewportKey !== nextKey) {
        stableHeight = rawHeight
        largeHeight = nextLargeHeight
        viewportKey = nextKey
      } else {
        stableHeight = Math.min(stableHeight || rawHeight, rawHeight)
        largeHeight = Math.max(largeHeight || nextLargeHeight, nextLargeHeight)
      }

      setDocumentViewportUnit({
        smallHeight: stableHeight,
        largeHeight
      })

      setViewport((prev) => {
        if (
          prev.width === rawWidth &&
          prev.height === stableHeight &&
          prev.largeHeight === largeHeight &&
          prev.isMobile === isMobile
        ) {
          return prev
        }

        return {
          width: rawWidth,
          height: stableHeight,
          largeHeight,
          isMobile
        }
      })
    }

    updateViewport()

    window.addEventListener('resize', updateViewport)
    window.addEventListener('orientationchange', updateViewport)
    window.visualViewport?.addEventListener('resize', updateViewport)

    return () => {
      window.removeEventListener('resize', updateViewport)
      window.removeEventListener('orientationchange', updateViewport)
      window.visualViewport?.removeEventListener('resize', updateViewport)
    }
  }, [mobileBreakpoint])

  return viewport
}

export default useStableViewport
